import { isEqual } from 'lodash-es'
import {
  DATA_EXTRACTION_FIELDS_CONFIG,
  type DataExtractionFieldName,
  type DataExtractionFieldType,
} from '~/entities/document'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { formatDateDefault } from '~/shared/lib/datetime'
import type { AuditTrailEventDescriptionField } from '../model'

const NO_DATA_PLACEHOLDER = '--'

/** Resolves the display label for a `select`-type field's raw value. */
function getSelectOptionLabel(fieldName: string | undefined, rawValue: string): string {
  if (!fieldName) return rawValue
  const config = DATA_EXTRACTION_FIELDS_CONFIG[fieldName as DataExtractionFieldName]
  if (!config || config.type !== 'select') return rawValue
  const match = config.options.find((option) => option.value === rawValue)
  return match ? match.label : rawValue
}

export function getDisplayValueOfField(
  value: string | (apiClient.CarcRarcCode | apiClient.TagInfo)[] | undefined,
  fieldType: DataExtractionFieldType | undefined,
  fieldName?: string,
): string | string[] {
  if (!value) {
    return '--'
  }
  switch (fieldType) {
    case 'textfield':
    case 'custom':
    case 'mandatory-comment':
      return value as string
    case 'date-input':
      return formatDateDefault(value as string)
    case 'select':
      return getSelectOptionLabel(fieldName, value as string)
    case 'carc':
    case 'rarc':
    case 'natera-tags':
      return (value as (apiClient.CarcRarcCode | apiClient.TagInfo)[]).map(
        (code) => `${code.code} - ${code.description}`,
      )
    default:
      return value ? (value as string) : NO_DATA_PLACEHOLDER
  }
}

/** A single field from data_content.fields */
type DataField = {
  name: string
  value: string | (apiClient.CarcRarcCode | apiClient.TagInfo)[]
}

/** Resolves field label from config, preferring shortLabel */
export function getFieldLabel(fieldName: string): string | undefined {
  const fieldConfig = DATA_EXTRACTION_FIELDS_CONFIG[fieldName as DataExtractionFieldName]
  if (!fieldConfig) return undefined
  return 'shortLabel' in fieldConfig ? fieldConfig.shortLabel : fieldConfig.label
}

/** Resolves field type from config */
export function getFieldType(fieldName: string): DataExtractionFieldType | undefined {
  return DATA_EXTRACTION_FIELDS_CONFIG[fieldName as DataExtractionFieldName]?.type
}

/**
 * Computes CARC/RARC/natera-tags diff fields between current and previous values.
 * Shared between data extraction and tagging transforms.
 */
export function computeCodeDiffFields({
  fieldLabel,
  fieldType,
  currentValues,
  prevValues,
  currentEventType,
  prevEventType,
}: {
  fieldLabel: string
  fieldType: DataExtractionFieldType
  currentValues: (apiClient.CarcRarcCode | apiClient.TagInfo)[]
  prevValues: (apiClient.CarcRarcCode | apiClient.TagInfo)[]
  currentEventType: string
  prevEventType: string | undefined
}): AuditTrailEventDescriptionField[] {
  const fields: AuditTrailEventDescriptionField[] = []
  const addedValues = currentValues.filter(
    (value) => !!value.code && !prevValues.map((v) => v.code).includes(value.code),
  )
  const removedValues = prevValues.filter(
    (value) => !!value.code && !currentValues.map((v) => v.code).includes(value.code),
  )
  // Show every field only for the first user submission
  if (['datallm', 'tagsllm'].includes(prevEventType ?? '')) {
    const nonChangedValues = currentValues.filter(
      (value) =>
        !!value.code &&
        !addedValues.map((v) => v.code).includes(value.code) &&
        !removedValues.map((v) => v.code).includes(value.code),
    )
    if (nonChangedValues.length > 0) {
      fields.push({
        label: `${fieldLabel}`,
        values: [
          {
            diff: getDisplayValueOfField(nonChangedValues, fieldType) as string[],
          },
        ],
      })
    }
  }
  if (addedValues.length > 0) {
    fields.push({
      label: `${fieldLabel}`,
      values: [
        {
          diff: getDisplayValueOfField(addedValues, fieldType) as string[],
        },
      ],
      action: ['datallm', 'tagsllm'].includes(currentEventType) ? 'suggested' : 'added',
    })
  }
  if (removedValues.length > 0) {
    fields.push({
      label: `${fieldLabel}`,
      values: [
        {
          diff: getDisplayValueOfField(removedValues, fieldType) as string[],
        },
      ],
      action: 'removed',
    })
  }
  return fields
}

/**
 * Computes CARC/RARC/natera-tags diff fields for tagging transform.
 * Same as computeCodeDiffFields but with 'suggested' action for non-changed values
 * on first user submission (matching original buildTaggingDetails behavior).
 */
export function computeTaggingCodeDiffFields({
  fieldLabel,
  fieldType,
  currentValues,
  prevValues,
  currentEventType,
  prevEventType,
}: {
  fieldLabel: string
  fieldType: DataExtractionFieldType
  currentValues: (apiClient.CarcRarcCode | apiClient.TagInfo)[]
  prevValues: (apiClient.CarcRarcCode | apiClient.TagInfo)[]
  currentEventType: string
  prevEventType: string | undefined
}): AuditTrailEventDescriptionField[] {
  const fields: AuditTrailEventDescriptionField[] = []
  const addedValues = currentValues.filter(
    (value) => !!value.code && !prevValues.map((v) => v.code).includes(value.code),
  )
  const removedValues = prevValues.filter(
    (value) => !!value.code && !currentValues.map((v) => v.code).includes(value.code),
  )
  // Show every field only for the first user submission
  if (['datallm', 'tagsllm'].includes(prevEventType ?? '')) {
    const nonChangedValues = currentValues.filter(
      (value) =>
        !!value.code &&
        !addedValues.map((v) => v.code).includes(value.code) &&
        !removedValues.map((v) => v.code).includes(value.code),
    )
    if (nonChangedValues.length > 0) {
      fields.push({
        label: `${fieldLabel}`,
        values: [
          {
            diff: getDisplayValueOfField(nonChangedValues, fieldType) as string[],
          },
        ],
        action: 'suggested',
      })
    }
  }
  if (addedValues.length > 0) {
    fields.push({
      label: `${fieldLabel}`,
      values: [
        {
          diff: getDisplayValueOfField(addedValues, fieldType) as string[],
        },
      ],
      action: ['datallm', 'tagsllm'].includes(currentEventType) ? 'suggested' : 'added',
    })
  }
  if (removedValues.length > 0) {
    fields.push({
      label: `${fieldLabel}`,
      values: [
        {
          diff: getDisplayValueOfField(removedValues, fieldType) as string[],
        },
      ],
      action: 'removed',
    })
  }
  return fields
}

/**
 * Computes text/date/custom field diffs.
 * Used by data extraction transform for non-code fields.
 */
export function computeScalarFieldDiff({
  fieldName,
  fieldLabel,
  fieldType,
  currentValue,
  prevField,
  prevEventType,
  initial,
}: {
  fieldName: string
  fieldLabel: string
  fieldType: DataExtractionFieldType | undefined
  currentValue: string | (apiClient.CarcRarcCode | apiClient.TagInfo)[] | undefined
  prevField: DataField | undefined
  prevEventType: string | undefined
  initial?: boolean
}): AuditTrailEventDescriptionField | null {
  if (initial) {
    // Show every field for opti event
    return {
      label: fieldLabel,
      values: [
        {
          diff: [getDisplayValueOfField(currentValue, fieldType, fieldName) as string],
        },
      ],
    }
  }
  if (isEqual(currentValue, prevField?.value)) {
    // Show every field only for the first user submission
    if (['datallm', 'tagsllm'].includes(prevEventType ?? '')) {
      return {
        label: fieldLabel,
        values: [
          {
            diff: [getDisplayValueOfField(currentValue, fieldType, fieldName) as string],
          },
        ],
      }
    }
    return null
  }
  if (prevField) {
    // Show field change if the field value has changed
    return {
      label: fieldLabel,
      values: [
        {
          previous: getDisplayValueOfField(prevField.value, fieldType, fieldName) as string,
          current: getDisplayValueOfField(currentValue, fieldType, fieldName) as string,
        },
      ],
    }
  }
  // Show field current value if field was added for validation and submitted
  return {
    label: fieldLabel,
    values: [
      {
        diff: [getDisplayValueOfField(currentValue, fieldType, fieldName) as string],
      },
    ],
    action: 'added',
  }
}

/**
 * Computes removed field entries (fields in prev but not in current).
 * Only shown for >1 user submission events.
 */
export function computeRemovedFields({
  prevDataFields,
  currentDataFields,
  prevEventType,
}: {
  prevDataFields: DataField[]
  currentDataFields: DataField[]
  prevEventType: string | undefined
}): AuditTrailEventDescriptionField[] {
  const fields: AuditTrailEventDescriptionField[] = []
  if (['datallm', 'tagsllm'].includes(prevEventType ?? '')) {
    return fields
  }
  const removedFields = prevDataFields.filter(
    (field) => !currentDataFields.find((f) => f.name === field.name),
  )
  removedFields.forEach((field) => {
    const fieldLabel = getFieldLabel(field.name)
    const fieldType = getFieldType(field.name)
    if (!fieldLabel) return
    fields.push({
      label: fieldLabel,
      values: [{ diff: [getDisplayValueOfField(field.value, fieldType, field.name) as string] }],
      action: 'removed',
    })
  })
  return fields
}
